import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faPlus,
  faEdit,
  faTrash,
  faUsers,
  faCheck,
  faTimes,
  faSortUp,
  faSortDown,
  faSort,
  faGlobe,
  faEnvelope,
  faCalendar,
} from '@fortawesome/free-solid-svg-icons';
// import apiService from '../services/api'; // eslint-disable-line no-unused-vars
import '../styles/AdminGroups.css';

const AdminGroups = () => {
  const [groups, setGroups] = useState([]);
  const [filteredGroups, setFilteredGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);

  // Mock groups data with real fansub logos
  const mockGroups = [
    {
      id: 1,
      name: 'AnimeVN Fansub',
      slug: 'animevn-fansub',
      description: 'Nhóm dịch anime hàng đầu Việt Nam',
      website: 'https://animevn.com',
      email: 'contact@animevn.com',
      logo: 'https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/72x72/1f1fb-1f1f3.png',
      movie_count: 45,
      status: 'active',
      founded_date: '2020-01-15',
      members_count: 12,
      created_at: '2024-01-15',
      updated_at: '2024-01-20'
    },
    {
      id: 2,
      name: 'VietSub Team',
      slug: 'vietsub-team',
      description: 'Chuyên dịch anime chất lượng cao',
      website: 'https://vietsubteam.net',
      email: 'admin@vietsubteam.net',
      logo: 'https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/72x72/1f4fa.png',
      movie_count: 32,
      status: 'active',
      founded_date: '2019-06-10',
      members_count: 8,
      created_at: '2024-01-12',
      updated_at: '2024-01-18'
    },
    {
      id: 3,
      name: 'Sakura Fansub',
      slug: 'sakura-fansub',
      description: 'Nhóm dịch anime romantic và slice of life',
      website: 'https://sakurafansub.org',
      email: 'hello@sakurafansub.org',
      logo: 'https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/72x72/1f338.png',
      movie_count: 28,
      status: 'active',
      founded_date: '2021-03-20',
      members_count: 6,
      created_at: '2024-01-10',
      updated_at: '2024-01-16'
    },
    {
      id: 4,
      name: 'Dragon Subs',
      slug: 'dragon-subs',
      description: 'Chuyên anime hành động và shounen',
      website: 'https://dragonsubs.com',
      email: 'contact@dragonsubs.com',
      logo: 'https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/72x72/1f409.png',
      movie_count: 15,
      status: 'inactive',
      founded_date: '2018-11-05',
      members_count: 4,
      created_at: '2024-01-08',
      updated_at: '2024-01-14'
    }
  ];

  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      setGroups(mockGroups);
      setFilteredGroups(mockGroups);
      setLoading(false);
    }, 1000);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    filterAndSortGroups();
  }, [searchTerm, sortField, sortDirection, groups]); // eslint-disable-line react-hooks/exhaustive-deps

  const filterAndSortGroups = () => {
    let filtered = [...groups];

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(group =>
        group.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        group.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        group.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sort
    filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredGroups(filtered);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleAddGroup = () => {
    setEditingGroup(null);
    setShowAddModal(true);
  };

  const handleEditGroup = (group) => {
    setEditingGroup(group);
    setShowAddModal(true);
  };

  const handleDeleteGroup = (groupId) => {
    if (window.confirm('Bạn có chắc chắn muốn xóa nhóm dịch này?')) {
      setGroups(groups.filter(group => group.id !== groupId));
    }
  };

  const handleToggleStatus = (groupId) => {
    setGroups(groups.map(group => {
      if (group.id === groupId) {
        return {
          ...group,
          status: group.status === 'active' ? 'inactive' : 'active'
        };
      }
      return group;
    }));
  };

  const handleSaveGroup = (groupData) => {
    if (editingGroup) {
      // Update existing group
      setGroups(groups.map(group => 
        group.id === editingGroup.id 
          ? { ...group, ...groupData, updated_at: new Date().toISOString().split('T')[0] }
          : group
      ));
    } else {
      // Add new group
      const newGroup = {
        id: Date.now(),
        ...groupData,
        movie_count: 0,
        created_at: new Date().toISOString().split('T')[0],
        updated_at: new Date().toISOString().split('T')[0]
      };
      setGroups([...groups, newGroup]);
    }
    setShowAddModal(false);
    setEditingGroup(null);
  };

  const handleCancelEdit = () => {
    setShowAddModal(false);
    setEditingGroup(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'warning';
      default: return 'secondary';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active': return 'Hoạt động';
      case 'inactive': return 'Tạm dừng';
      default: return 'Không xác định';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('vi-VN');
  };

  if (loading) {
    return (
      <div className="admin-groups">
        <div className="groups-loading">
          <div className="loading-spinner"></div>
          <p>Đang tải danh sách nhóm dịch...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-groups">
      <div className="groups-header">
        <div className="header-left">
          <h1>
            <FontAwesomeIcon icon={faUsers} />
            Quản lý Nhóm dịch
          </h1>
          <p>Quản lý các nhóm dịch anime trong hệ thống</p>
        </div>
        <div className="header-actions">
          <button className="add-group-btn" onClick={handleAddGroup}>
            <FontAwesomeIcon icon={faPlus} />
            Thêm nhóm dịch
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="group-stats">
        <div className="stat-card">
          <div className="stat-icon success">
            <FontAwesomeIcon icon={faUsers} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{groups.filter(g => g.status === 'active').length}</div>
            <div className="stat-label">Nhóm hoạt động</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon warning">
            <FontAwesomeIcon icon={faTimes} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{groups.filter(g => g.status === 'inactive').length}</div>
            <div className="stat-label">Nhóm tạm dừng</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon primary">
            <FontAwesomeIcon icon={faCheck} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{groups.reduce((sum, g) => sum + g.movie_count, 0)}</div>
            <div className="stat-label">Tổng phim</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon info">
            <FontAwesomeIcon icon={faUsers} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{groups.reduce((sum, g) => sum + g.members_count, 0)}</div>
            <div className="stat-label">Tổng thành viên</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="groups-filters">
        <div className="search-box">
          <FontAwesomeIcon icon={faSearch} />
          <input
            type="text"
            placeholder="Tìm kiếm nhóm dịch..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Groups Table */}
      <div className="groups-table-container">
        <table className="groups-table">
          <thead>
            <tr>
              <th>Logo</th>
              <th onClick={() => handleSort('name')} className="sortable">
                Tên nhóm
                <FontAwesomeIcon 
                  icon={sortField === 'name' ? (sortDirection === 'asc' ? faSortUp : faSortDown) : faSort} 
                />
              </th>
              <th>Thông tin</th>
              <th onClick={() => handleSort('movie_count')} className="sortable">
                Số phim
                <FontAwesomeIcon 
                  icon={sortField === 'movie_count' ? (sortDirection === 'asc' ? faSortUp : faSortDown) : faSort} 
                />
              </th>
              <th onClick={() => handleSort('members_count')} className="sortable">
                Thành viên
                <FontAwesomeIcon 
                  icon={sortField === 'members_count' ? (sortDirection === 'asc' ? faSortUp : faSortDown) : faSort} 
                />
              </th>
              <th onClick={() => handleSort('status')} className="sortable">
                Trạng thái
                <FontAwesomeIcon 
                  icon={sortField === 'status' ? (sortDirection === 'asc' ? faSortUp : faSortDown) : faSort} 
                />
              </th>
              <th onClick={() => handleSort('founded_date')} className="sortable">
                Thành lập
                <FontAwesomeIcon 
                  icon={sortField === 'founded_date' ? (sortDirection === 'asc' ? faSortUp : faSortDown) : faSort} 
                />
              </th>
              <th>Thao tác</th>
            </tr>
          </thead>
          <tbody>
            {filteredGroups.map(group => (
              <tr key={group.id}>
                <td>
                  <div className="group-logo">
                    <img 
                      src={group.logo} 
                      alt={group.name}
                      onError={(e) => {
                        e.target.src = '/logo.png';
                      }}
                    />
                  </div>
                </td>
                <td>
                  <div className="group-info">
                    <div className="group-name">{group.name}</div>
                    <div className="group-slug">/{group.slug}</div>
                  </div>
                </td>
                <td>
                  <div className="group-details">
                    <div className="group-description">{group.description}</div>
                    <div className="group-contacts">
                      {group.website && (
                        <a href={group.website} target="_blank" rel="noopener noreferrer">
                          <FontAwesomeIcon icon={faGlobe} />
                          Website
                        </a>
                      )}
                      {group.email && (
                        <a href={`mailto:${group.email}`}>
                          <FontAwesomeIcon icon={faEnvelope} />
                          Email
                        </a>
                      )}
                    </div>
                  </div>
                </td>
                <td>
                  <div className="movie-count">
                    <span className="count-number">{group.movie_count}</span>
                    <span className="count-label">phim</span>
                  </div>
                </td>
                <td>
                  <div className="members-count">
                    <span className="count-number">{group.members_count}</span>
                    <span className="count-label">người</span>
                  </div>
                </td>
                <td>
                  <div className={`status-badge ${getStatusColor(group.status)}`}>
                    {getStatusText(group.status)}
                  </div>
                </td>
                <td>
                  <div className="founded-date">
                    <FontAwesomeIcon icon={faCalendar} />
                    {formatDate(group.founded_date)}
                  </div>
                </td>
                <td>
                  <div className="action-buttons">
                    <button 
                      className={`action-btn toggle ${group.status}`}
                      title={group.status === 'active' ? 'Tạm dừng' : 'Kích hoạt'}
                      onClick={() => handleToggleStatus(group.id)}
                    >
                      <FontAwesomeIcon icon={group.status === 'active' ? faTimes : faCheck} />
                    </button>
                    <button 
                      className="action-btn edit"
                      title="Chỉnh sửa"
                      onClick={() => handleEditGroup(group)}
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                    <button 
                      className="action-btn delete"
                      title="Xóa"
                      onClick={() => handleDeleteGroup(group.id)}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Group Form Modal */}
      {showAddModal && (
        <GroupForm
          group={editingGroup}
          onSave={handleSaveGroup}
          onCancel={handleCancelEdit}
          isOpen={showAddModal}
        />
      )}
    </div>
  );
};

// Group Form Component
const GroupForm = ({ group, onSave, onCancel, isOpen }) => {
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    website: '',
    email: '',
    logo: '',
    founded_date: '',
    members_count: 1,
    status: 'active'
  });

  useEffect(() => {
    if (group) {
      setFormData({
        name: group.name || '',
        slug: group.slug || '',
        description: group.description || '',
        website: group.website || '',
        email: group.email || '',
        logo: group.logo || '',
        founded_date: group.founded_date || '',
        members_count: group.members_count || 1,
        status: group.status || 'active'
      });
    } else {
      setFormData({
        name: '',
        slug: '',
        description: '',
        website: '',
        email: '',
        logo: '',
        founded_date: '',
        members_count: 1,
        status: 'active'
      });
    }
  }, [group]);

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim('-');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const newFormData = {
      ...formData,
      [name]: value
    };
    
    // Auto-generate slug when name changes
    if (name === 'name') {
      newFormData.slug = generateSlug(value);
    }
    
    setFormData(newFormData);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content group-form-modal">
        <div className="modal-header">
          <h3>
            <FontAwesomeIcon icon={faUsers} />
            {group ? 'Chỉnh sửa nhóm dịch' : 'Thêm nhóm dịch mới'}
          </h3>
          <button className="modal-close" onClick={onCancel}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="group-form">
          <div className="form-grid">
            <div className="form-group">
              <label>Tên nhóm *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="VD: AnimeVN Fansub"
                required
              />
            </div>

            <div className="form-group">
              <label>Slug *</label>
              <input
                type="text"
                name="slug"
                value={formData.slug}
                onChange={handleChange}
                placeholder="animevn-fansub"
                required
              />
            </div>

            <div className="form-group full-width">
              <label>Mô tả</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Mô tả về nhóm dịch..."
                rows={3}
              />
            </div>

            <div className="form-group">
              <label>Website</label>
              <input
                type="url"
                name="website"
                value={formData.website}
                onChange={handleChange}
                placeholder="https://example.com"
              />
            </div>

            <div className="form-group">
              <label>Email liên hệ</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="contact@example.com"
              />
            </div>

            <div className="form-group">
              <label>Logo URL</label>
              <input
                type="url"
                name="logo"
                value={formData.logo}
                onChange={handleChange}
                placeholder="/logos/group.png"
              />
            </div>

            <div className="form-group">
              <label>Ngày thành lập</label>
              <input
                type="date"
                name="founded_date"
                value={formData.founded_date}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>Số thành viên</label>
              <input
                type="number"
                name="members_count"
                value={formData.members_count}
                onChange={handleChange}
                min="1"
              />
            </div>

            <div className="form-group">
              <label>Trạng thái</label>
              <select name="status" value={formData.status} onChange={handleChange}>
                <option value="active">Hoạt động</option>
                <option value="inactive">Tạm dừng</option>
              </select>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={onCancel}>
              <FontAwesomeIcon icon={faTimes} />
              Hủy
            </button>
            <button type="submit" className="btn-save">
              <FontAwesomeIcon icon={faCheck} />
              {group ? 'Cập nhật' : 'Thêm mới'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminGroups;